/** Admin panel for managing knowledge sources (Drive files/folders) per plugin. */

import { useState } from "react";
import {
  Box, Typography, Stack, List, ListItem, ListItemIcon, ListItemText,
  ListItemSecondaryAction, IconButton, Button, Chip, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButton, ToggleButtonGroup, Alert, Tooltip, CircularProgress,
} from "@mui/material";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import {
  usePluginKnowledge,
  useAddKnowledgeSource,
  useDeleteKnowledgeSource,
  useValidateKnowledgeSource,
} from "@/hooks/useAdmin";
import type { KnowledgeSource } from "@/types";

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface KnowledgeSourcePanelProps {
  pluginId: string;
}

export default function KnowledgeSourcePanel({ pluginId }: KnowledgeSourcePanelProps) {
  const { data, isLoading } = usePluginKnowledge(pluginId);
  const addSource = useAddKnowledgeSource();
  const deleteSource = useDeleteKnowledgeSource();
  const validateSource = useValidateKnowledgeSource();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeSource | null>(null);
  const [validationResults, setValidationResults] = useState<Record<string, { valid: boolean; message: string }>>({});

  // Add form state
  const [sourceType, setSourceType] = useState<"file" | "folder">("file");
  const [driveId, setDriveId] = useState("");
  const [label, setLabel] = useState("");
  const [maxFiles, setMaxFiles] = useState("20");

  const sources = data?.sources ?? [];
  const totalSize = sources.reduce((sum, s) => sum + (s.sizeBytes ?? 0), 0);
  const usagePercent = (totalSize / MAX_SIZE_BYTES) * 100;

  const handleAdd = () => {
    const body: Record<string, unknown> = {
      pluginId,
      sourceType,
      label,
    };
    if (sourceType === "file") {
      body.driveFileId = driveId;
    } else {
      body.driveFolderId = driveId;
      body.maxFiles = parseInt(maxFiles, 10) || 20;
    }
    addSource.mutate(body as { pluginId: string } & Record<string, unknown>, {
      onSuccess: () => {
        setAddDialogOpen(false);
        resetForm();
      },
    });
  };

  const handleDelete = () => {
    if (deleteTarget) {
      deleteSource.mutate(
        { pluginId, sourceId: deleteTarget.sourceId },
        { onSuccess: () => setDeleteTarget(null) },
      );
    }
  };

  const handleValidate = (source: KnowledgeSource) => {
    validateSource.mutate(
      {
        pluginId,
        sourceType: source.sourceType,
        driveFileId: source.driveFileId,
        driveFolderId: source.driveFolderId,
      },
      {
        onSuccess: (result) => {
          setValidationResults((prev) => ({
            ...prev,
            [source.sourceId]: {
              valid: result.valid,
              message: result.valid
                ? `Valid: ${result.name} (${result.mimeType}, ${formatBytes(result.size ?? 0)})`
                : `Error: ${result.error}`,
            },
          }));
        },
        onError: (err) => {
          setValidationResults((prev) => ({
            ...prev,
            [source.sourceId]: { valid: false, message: err.message },
          }));
        },
      },
    );
  };

  const resetForm = () => {
    setSourceType("file");
    setDriveId("");
    setLabel("");
    setMaxFiles("20");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Knowledge Base</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatBytes(totalSize)} / {formatBytes(MAX_SIZE_BYTES)}
          </Typography>
        </Box>
        <Button
          startIcon={<AddCircleOutline />}
          variant="contained"
          size="small"
          onClick={() => setAddDialogOpen(true)}
        >
          Add Source
        </Button>
      </Stack>

      {/* Usage indicator */}
      <LinearProgress
        variant="determinate"
        value={Math.min(usagePercent, 100)}
        sx={{ mb: 2, height: 8, borderRadius: 4 }}
      />

      {/* Source list or empty state */}
      {sources.length === 0 ? (
        <Alert severity="info" data-testid="empty-state">
          No knowledge sources configured. Add Drive files or folders to provide context for Gemini.
        </Alert>
      ) : (
        <List dense>
          {sources.map((source) => (
            <ListItem key={source.sourceId} sx={{ pr: 16 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {source.sourceType === "file" ? (
                  <InsertDriveFileOutlined />
                ) : (
                  <FolderOutlined />
                )}
              </ListItemIcon>
              <ListItemText
                primary={source.label}
                secondary={
                  <>
                    {source.sourceType === "file" ? source.driveFileId : source.driveFolderId}
                    {source.sizeBytes != null && ` - ${formatBytes(source.sizeBytes)}`}
                  </>
                }
              />
              <Chip
                size="small"
                label={source.status}
                color={source.status === "active" ? "success" : "error"}
                icon={source.status === "active" ? <CheckCircleOutline /> : <ErrorOutline />}
                sx={{ mr: 1 }}
              />
              <ListItemSecondaryAction>
                <Tooltip title={validationResults[source.sourceId]?.message ?? "Validate source"}>
                  <IconButton
                    size="small"
                    onClick={() => handleValidate(source)}
                    disabled={validateSource.isPending}
                    aria-label="Validate source"
                  >
                    <VerifiedOutlined
                      fontSize="small"
                      color={
                        validationResults[source.sourceId]?.valid === true
                          ? "success"
                          : validationResults[source.sourceId]?.valid === false
                            ? "error"
                            : "action"
                      }
                    />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => setDeleteTarget(source)}
                  aria-label="Delete source"
                >
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Add Source Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Knowledge Source</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {addSource.isError && (
            <Alert severity="error">{addSource.error.message}</Alert>
          )}
          <ToggleButtonGroup
            value={sourceType}
            exclusive
            onChange={(_, val) => val && setSourceType(val)}
            size="small"
            fullWidth
          >
            <ToggleButton value="file">
              <InsertDriveFileOutlined sx={{ mr: 0.5 }} /> File
            </ToggleButton>
            <ToggleButton value="folder">
              <FolderOutlined sx={{ mr: 0.5 }} /> Folder
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label={sourceType === "file" ? "Drive File ID" : "Drive Folder ID"}
            value={driveId}
            onChange={(e) => setDriveId(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            size="small"
            fullWidth
            required
            helperText="A descriptive name for this knowledge source"
          />
          {sourceType === "folder" && (
            <TextField
              label="Max Files"
              value={maxFiles}
              onChange={(e) => setMaxFiles(e.target.value)}
              size="small"
              type="number"
              fullWidth
              helperText="Maximum number of files to include from this folder (default 20)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!driveId || !label || addSource.isPending}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Knowledge Source?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteTarget?.label}</strong> from this plugin's knowledge base? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteSource.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
